import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Briefcase, MapPin, Sparkles, ChevronRight, Linkedin, Twitter, Instagram, Youtube, Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicCompanyLanding() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/public/landing/${slug}`)
            .then((r) => setData(r.data))
            .catch((err) => setError(err?.response?.data?.detail || "Landing page not found"));
    }, [slug]);

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-secondary/30">
                <div className="text-center">
                    <h1 className="font-display font-black text-4xl text-foreground/40">404</h1>
                    <p className="text-foreground/60 mt-2">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="min-h-screen flex items-center justify-center text-sm font-mono text-foreground/60">Loading…</div>;
    }

    const { company, landing, jobs = [], theme = {} } = data;
    const primary = theme.primary_color || "#002FA7";
    const accent = theme.accent_color || primary;
    const bg = theme.bg_color || "#FFFFFF";
    const text = theme.text_color || "#0F172A";

    return (
        <div style={{ backgroundColor: bg, color: text }} data-testid="public-landing">
            {/* Header */}
            <header className="border-b border-border/40 bg-white">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {company.logo_url ? (
                            <img src={company.logo_url} alt={company.name} className="h-10 w-auto" />
                        ) : (
                            <Building2 className="w-7 h-7" style={{ color: primary }} />
                        )}
                        <span className="font-display font-bold text-lg">{company.name}</span>
                    </div>
                    <a href="#jobs" className="text-sm font-mono uppercase tracking-widest hover:opacity-80" data-testid="landing-jobs-link">
                        Open roles {jobs.length > 0 && <span className="bg-foreground/10 px-1.5 ml-1 rounded">{jobs.length}</span>}
                    </a>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-8 items-center">
                <div>
                    {landing.hero_eyebrow && (
                        <div className="text-xs font-mono uppercase tracking-widest" style={{ color: primary }}>
                            {landing.hero_eyebrow}
                        </div>
                    )}
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-tight mt-3" data-testid="landing-h1">
                        {landing.hero_title || "Build with us."}
                    </h1>
                    {landing.hero_subtitle && (
                        <p className="text-foreground/70 text-lg mt-5 max-w-xl">{landing.hero_subtitle}</p>
                    )}
                    <a href="#jobs" className="mt-7 inline-flex items-center gap-2 px-6 py-3 text-sm font-mono uppercase tracking-widest text-white" style={{ backgroundColor: primary }} data-testid="landing-cta">
                        {landing.hero_cta_label || "See open roles"} <ChevronRight className="w-4 h-4" />
                    </a>
                </div>
                {landing.hero_image_url && (
                    <div className="hidden lg:block">
                        <img src={landing.hero_image_url} alt="" className="w-full h-auto border border-border" />
                    </div>
                )}
            </section>

            {/* Stats */}
            {(landing.stats || []).length > 0 && (
                <section className="border-y border-border bg-secondary/30 py-12">
                    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
                        {landing.stats.map((s, i) => (
                            <div key={i} data-testid={`landing-stat-${i}`}>
                                <div className="font-display font-black text-3xl sm:text-4xl" style={{ color: primary }}>{s.value}</div>
                                <div className="text-xs font-mono uppercase tracking-widest text-foreground/70 mt-1">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* About */}
            {(landing.about_title || landing.about_body) && (
                <section className="max-w-[900px] mx-auto px-4 sm:px-6 py-16">
                    {landing.about_title && <h2 className="font-display font-black text-3xl sm:text-4xl">{landing.about_title}</h2>}
                    {landing.about_body && <p className="text-foreground/80 text-base sm:text-lg mt-4 whitespace-pre-line">{landing.about_body}</p>}
                </section>
            )}

            {/* Features */}
            {(landing.features || []).length > 0 && (
                <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16">
                    <h2 className="font-display font-black text-3xl mb-8">Why join us</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                        {landing.features.map((f, i) => (
                            <div key={i} className="bg-white p-6" data-testid={`landing-feature-${i}`}>
                                <Sparkles className="w-5 h-5 mb-3" style={{ color: accent }} />
                                <h3 className="font-display font-bold text-lg">{f.title}</h3>
                                <p className="text-foreground/70 text-sm mt-2">{f.body}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Testimonials */}
            {(landing.testimonials || []).length > 0 && (
                <section className="bg-secondary/30 py-16 border-y border-border">
                    <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
                        <h2 className="font-display font-black text-3xl mb-8">From the team</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {landing.testimonials.map((t, i) => (
                                <figure key={i} className="bg-white border border-border p-6" data-testid={`landing-testimonial-${i}`}>
                                    <blockquote className="text-base italic">“{t.quote}”</blockquote>
                                    <figcaption className="mt-4 text-sm">
                                        <div className="font-bold">{t.author}</div>
                                        {t.role && <div className="text-foreground/60 text-xs">{t.role}</div>}
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Jobs */}
            {landing.show_jobs_section !== false && (
                <section id="jobs" className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16">
                    <h2 className="font-display font-black text-3xl mb-8 flex items-center gap-3"><Briefcase className="w-7 h-7" style={{ color: primary }} /> Open roles</h2>
                    {jobs.length === 0 ? (
                        <div className="border border-dashed border-border bg-white p-12 text-center text-foreground/60">
                            No open roles right now — check back soon.
                        </div>
                    ) : (
                        <div className="divide-y divide-border border border-border bg-white">
                            {jobs.map((j) => (
                                <Link to={`/j/${j.slug}`} key={j.id} className="block p-4 sm:p-5 hover:bg-secondary/40 flex items-center justify-between gap-4" data-testid={`landing-job-${j.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold text-lg truncate">{j.title}</div>
                                        <div className="text-xs font-mono uppercase tracking-widest text-foreground/60 mt-1 flex items-center gap-2 flex-wrap">
                                            {j.department && <span>{j.department}</span>}
                                            {j.work_mode && <><span>·</span><span>{j.work_mode}</span></>}
                                            {j.location && <><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span></>}
                                            {j.employment_type && <><span>·</span><span>{j.employment_type}</span></>}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-foreground/40 shrink-0" />
                                </Link>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Talent pool CTA */}
            {landing.show_talent_pool_cta && company.talent_pool_slug && (
                <section className="py-16 border-t border-border" style={{ backgroundColor: `${primary}0d` }}>
                    <div className="max-w-[900px] mx-auto px-4 sm:px-6 text-center">
                        <h2 className="font-display font-black text-3xl">{landing.talent_pool_cta_title || "Don't see a fit?"}</h2>
                        <p className="text-foreground/70 mt-3 max-w-xl mx-auto">{landing.talent_pool_cta_body || "Submit your resume — we'll reach out when a matching role opens."}</p>
                        <Link to={`/p/${company.talent_pool_slug}`} className="mt-6 inline-flex items-center gap-2 px-6 py-3 text-sm font-mono uppercase tracking-widest text-white" style={{ backgroundColor: primary }} data-testid="landing-pool-cta">
                            Submit your resume <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="border-t border-border bg-white py-8">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex items-center justify-between flex-wrap gap-4">
                    <div className="text-xs text-foreground/60">
                        {landing.footer_text || `© ${new Date().getFullYear()} ${company.name}`}
                    </div>
                    <div className="flex items-center gap-3">
                        {landing.social_links?.linkedin && <a href={landing.social_links.linkedin} target="_blank" rel="noreferrer" className="text-foreground/60 hover:text-foreground" data-testid="landing-social-linkedin"><Linkedin className="w-4 h-4" /></a>}
                        {landing.social_links?.twitter && <a href={landing.social_links.twitter} target="_blank" rel="noreferrer" className="text-foreground/60 hover:text-foreground" data-testid="landing-social-twitter"><Twitter className="w-4 h-4" /></a>}
                        {landing.social_links?.instagram && <a href={landing.social_links.instagram} target="_blank" rel="noreferrer" className="text-foreground/60 hover:text-foreground" data-testid="landing-social-instagram"><Instagram className="w-4 h-4" /></a>}
                        {landing.social_links?.youtube && <a href={landing.social_links.youtube} target="_blank" rel="noreferrer" className="text-foreground/60 hover:text-foreground" data-testid="landing-social-youtube"><Youtube className="w-4 h-4" /></a>}
                    </div>
                </div>
            </footer>
        </div>
    );
}
